import { useMemo } from 'react'

import { Wizard, WizardStep } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { WorkingConditions, WorkingConditionsKeys } from 'modules/job_catalog/lib/workingConditions'

import { conditionsStep } from './ConditionsStep'
import { generalInformationStep } from './GeneralInformationStep'
import { WizardWorkingConditions } from './models/wizardWorkingConditions'

interface WorkingConditionsModalProps {
  nodeName: string
  initialWorkingConditions?: WorkingConditions
  onSubmit: (data: WorkingConditions) => Promise<void>
  onClose: () => void
}

export const WorkingConditionsModal = ({
  nodeName,
  initialWorkingConditions,
  onSubmit,
  onClose,
}: WorkingConditionsModalProps) => {
  const handleSubmit = async (data: WizardWorkingConditions) => {
    await onSubmit(parseWizardDataToWorkingConditions(data))
  }

  const initialData = useMemo(
    () => parseWorkingConditionsToWizardData(initialWorkingConditions),
    [initialWorkingConditions]
  )

  const isNewWorkingCondition = useMemo(
    () => Boolean(!initialWorkingConditions || initialWorkingConditions.inherited),
    [initialWorkingConditions]
  )

  const steps: WizardStep<WizardWorkingConditions>[] = [
    generalInformationStep(nodeName),
    conditionsStep(isNewWorkingCondition),
  ]

  return (
    <Wizard.Modal
      title={
        isNewWorkingCondition
          ? i18n.t('job_catalog.nodes.working_conditions.modal.new')
          : i18n.t('job_catalog.nodes.working_conditions.modal.edit')
      }
      description={i18n.t('job_catalog.nodes.working_conditions.modal.description', { nodeName })}
      icon="document-edit"
      steps={steps}
      initialData={initialData}
      onSubmit={handleSubmit}
      onClose={onClose}
      size={'large'}
    />
  )
}

const parseWizardDataToWorkingConditions = (data: WizardWorkingConditions): WorkingConditions => {
  const workingConditions: WorkingConditions = {
    id: data.id,
    name: data.name,
    description: data.description,
    conditions: [],
  }
  if (data.maxAnnualHours)
    workingConditions.conditions.push({
      key: WorkingConditionsKeys.MAX_ANNUAL_HOURS,
      value: data.maxAnnualHours,
    })
  if (data.minRestHoursBetweenDays)
    workingConditions.conditions.push({
      key: WorkingConditionsKeys.MIN_REST_HOURS_BETWEEN_DAYS,
      value: data.minRestHoursBetweenDays,
    })
  if (data.maxDailyHours)
    workingConditions.conditions.push({
      key: WorkingConditionsKeys.MAX_DAILY_HOURS,
      value: data.maxDailyHours,
    })
  if (data.maxWeeklyHours)
    workingConditions.conditions.push({
      key: WorkingConditionsKeys.MAX_WEEKLY_HOURS,
      value: data.maxWeeklyHours,
    })
  if (data.bankHolidaysTreatment)
    workingConditions.conditions.push({
      key: WorkingConditionsKeys.BANK_HOLIDAYS_TREATMENT,
      value: data.bankHolidaysTreatment,
    })
  return workingConditions
}

const parseWorkingConditionsToWizardData = (
  workingConditions?: WorkingConditions
): WizardWorkingConditions => {
  if (!workingConditions) return { name: '' }
  const data: WizardWorkingConditions = {
    id: workingConditions.id,
    name: workingConditions.name,
    description: workingConditions.description,
  }
  workingConditions.conditions.forEach((workingCondition) => {
    if (workingCondition.key === WorkingConditionsKeys.MAX_ANNUAL_HOURS && workingCondition.value)
      data.maxAnnualHours = +workingCondition.value
    if (
      workingCondition.key === WorkingConditionsKeys.MIN_REST_HOURS_BETWEEN_DAYS &&
      workingCondition.value
    )
      data.minRestHoursBetweenDays = +workingCondition.value
    if (workingCondition.key === WorkingConditionsKeys.MAX_DAILY_HOURS && workingCondition.value)
      data.maxDailyHours = +workingCondition.value
    if (workingCondition.key === WorkingConditionsKeys.MAX_WEEKLY_HOURS && workingCondition.value)
      data.maxWeeklyHours = +workingCondition.value
    if (
      workingCondition.key === WorkingConditionsKeys.BANK_HOLIDAYS_TREATMENT &&
      workingCondition.value
    )
      data.bankHolidaysTreatment = String(workingCondition.value)
  })

  // From inherited WC, we take conditions but not the name or description
  if (workingConditions.inherited) {
    delete data.id
    data.name = ''
    delete data.description
  }
  return data
}
